/**
 * The /mcp OAuth-clients panel's one-time connect-code reveal, and the label
 * of the per-client button that mints the code.
 *
 * A connect code binds a browser-OAuth connector's registration to one
 * `oauth_clients` grant on the approval page (`apps/mcp-server`'s consent
 * page). The code is shown ONCE and only its hash is stored, so the page keeps
 * this reveal mounted until its own Dismiss.
 *
 * This component lives in its own module (no `api` / supabase import) so it
 * can be render-tested in isolation, as `ScheduledJobsSection` is.
 */

export const MCP_CONNECTOR_URL = "https://api.hercie.ai/mcp";

export const CONNECT_CODE_BUTTON_LABEL = "Get connect code…";

export const CONNECT_CODE_BUTTON_TITLE =
  "Mint a one-time code to connect ChatGPT, Claude or another browser-OAuth MCP client to this grant.";

export interface ConnectCodeRevealProps {
  /** The grant's display name. */
  readonly name: string;
  /** The one-time connect code. */
  readonly code: string;
  /** The code's expiry, already formatted for display. */
  readonly expiresLabel: string;
  readonly copied: boolean;
  readonly onCopy: () => void;
  readonly onDismiss: () => void;
  /**
   * Set when the grant acts as a PERSON (migration 223) — always the person
   * looking at this reveal, since only they can mint its code. The wording
   * then changes from "send this to whoever is connecting" to "this is you;
   * send it to nobody".
   */
  readonly actsAs?: {
    readonly name: string;
    readonly tier: "staff" | "admin";
  };
}

export function ConnectCodeReveal({
  name,
  code,
  expiresLabel,
  copied,
  onCopy,
  onDismiss,
  actsAs,
}: ConnectCodeRevealProps): JSX.Element {
  return (
    <div className="oauth-secret-reveal">
      <h3>Connect code for {name} — shown ONCE</h3>
      {actsAs === undefined ? (
        <p className="hint">
          Send this to the person connecting (over something private — it
          grants this client&apos;s access). They add a connector with the URL{" "}
          <code>{MCP_CONNECTOR_URL}</code>: in ChatGPT web under Settings →
          Connectors; in Claude under Customize → Connectors, choosing{" "}
          <strong>Sign in now</strong> and{" "}
          <strong>Register automatically</strong>. Then they paste this code on
          the Clavis approval page. It works once and expires {expiresLabel}.
        </p>
      ) : (
        <p className="hint">
          <strong>
            Whatever you connect with this code acts as you ({actsAs.name}) at
            the {actsAs.tier} tier.
          </strong>{" "}
          Use it yourself and send it to nobody. Add a connector with the URL{" "}
          <code>{MCP_CONNECTOR_URL}</code>: in ChatGPT web under Settings →
          Connectors; in Claude under Customize → Connectors, choosing{" "}
          <strong>Sign in now</strong> and{" "}
          <strong>Register automatically</strong>. Then paste this code on the
          Clavis approval page. It works once and expires {expiresLabel}.
        </p>
      )}
      <div className="oauth-secret-line">
        <span>connector URL</span>
        <code>{MCP_CONNECTOR_URL}</code>
      </div>
      <div className="oauth-secret-line">
        <span>connect code</span>
        <code>{code}</code>
      </div>
      <div className="oauth-secret-actions">
        <button type="button" onClick={onCopy}>
          {copied ? "Copied ✓" : "Copy code"}
        </button>
        <button type="button" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
